import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useHeader } from '../context/HeaderContext';

export default function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const { showBackButton, backButtonAction } = useHeader();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  const scrollToFeatures = () => {
    if (location.pathname === '/') {
      const featuresSection = document.getElementById('features');
      if (featuresSection) {
        featuresSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/#features', { replace: true });
      setTimeout(() => {
        const featuresSection = document.getElementById('features');
        if (featuresSection) {
          featuresSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    closeMobileMenu();
  };

  const handleRevealClick = () => {
    if (isAuthenticated) {
      navigate('/clarity');
    } else {
      navigate('/login', { state: { from: { pathname: '/storylines' } } });
    }
    closeMobileMenu();
  };

  const handleDashboardClick = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/login', { state: { from: { pathname: '/storylines' } } });
    }
  };

  const handleBackClick = () => {
    if (backButtonAction) {
      backButtonAction();
    } else {
      navigate('/storylines');
    }
    closeMobileMenu();
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const getUserInitials = (username: string) => {
    return username.charAt(0).toUpperCase();
  };

  return (
    <>
      {/* Floating Back Button */}
      {showBackButton && (
        <motion.div
          className="fixed top-24 left-8 z-50"
          initial={{ opacity: 0, x: -100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <button
            onClick={handleBackClick}
            style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: 400,
              color: '#475569',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
            className="hover:bg-gray-50 hover:text-gray-900"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </motion.div>
      )}

      {/* Option 3: With Quick Actions Header */}
      <header 
        style={{
          background: 'white',
          borderBottom: '1px solid #e2e8f0',
          padding: '16px 24px',
          position: 'sticky',
          top: 0,
          zIndex: 40
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '1400px', margin: '0 auto' }}>
          {/* Left Section: Logo + Nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '40px' }}>
            <div 
              onClick={() => navigate('/dashboard')}
              style={{
                fontSize: '20px',
                fontWeight: 300,
                color: '#1e293b',
                letterSpacing: '-0.5px',
                cursor: 'pointer'
              }}
            >
              Mirray
            </div>
            <nav className="hidden md:flex gap-8 items-center desktop-nav" aria-hidden="true">
              <button
                onClick={handleDashboardClick}
                style={{
                  color: isActive('/dashboard') ? '#1e293b' : '#64748b',
                  fontWeight: isActive('/dashboard') ? 500 : 400,
                  fontSize: '14px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'color 0.2s'
                }}
                className="hover:text-gray-900"
              >
                Home
              </button>
              <button
                onClick={() => navigate('/catalog')}
                style={{
                  color: isActive('/catalog') || isActive('/storylines') ? '#1e293b' : '#64748b',
                  fontWeight: isActive('/catalog') || isActive('/storylines') ? 500 : 400,
                  fontSize: '14px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'color 0.2s'
                }}
                className="hover:text-gray-900"
              >
                Catalog
              </button>
              <button
                onClick={scrollToFeatures}
                style={{
                  color: location.pathname === '/' && location.hash === '#features' ? '#1e293b' : '#64748b',
                  fontWeight: location.pathname === '/' && location.hash === '#features' ? 500 : 400,
                  fontSize: '14px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'color 0.2s'
                }}
                className="hover:text-gray-900"
              >
                About
              </button>
            </nav>
          </div>

          {/* Right Section: Quick Actions + User */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Quick Action: New Reveal */}
            {isAuthenticated && (
              <button
                onClick={handleRevealClick}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  background: '#1e293b',
                  color: 'white',
                  fontSize: '13px',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  fontWeight: 400
                }}
                className="hover:bg-gray-900 hidden md:flex"
              >
                <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                </svg>
                New
              </button>
            )}

            {/* User Avatar */}
            {isAuthenticated ? (
              <div className="relative">
                <div
                  onClick={toggleUserMenu}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    color: '#475569',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'transform 0.2s'
                  }}
                  className="hover:scale-105"
                >
                  {getUserInitials(user?.username || 'U')}
                </div>

                {/* User Dropdown */}
                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        right: 0,
                        width: '220px',
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                        overflow: 'hidden'
                      }}
                      initial={{ opacity: 0, y: -8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                    >
                      <div style={{ padding: '16px', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ fontWeight: 500, color: '#1e293b', fontSize: '14px' }}>{user?.username}</div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Premium Member</div>
                      </div>
                      <div style={{ padding: '4px' }}>
                        <Link
                          to="/profile"
                          onClick={() => setIsUserMenuOpen(false)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            fontSize: '13px',
                            color: '#475569',
                            textDecoration: 'none',
                            borderRadius: '6px',
                            transition: 'background 0.2s'
                          }}
                          className="hover:bg-gray-50"
                        >
                          <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          Profile & Settings
                        </Link>
                        <Link
                          to="/insights"
                          onClick={() => setIsUserMenuOpen(false)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            fontSize: '13px',
                            color: '#475569',
                            textDecoration: 'none',
                            borderRadius: '6px',
                            transition: 'background 0.2s'
                          }}
                          className="hover:bg-gray-50"
                        >
                          <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          AI Insights
                        </Link>
                        <button
                          onClick={handleLogout}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            fontSize: '13px',
                            color: '#b91c1c',
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            width: '100%',
                            textAlign: 'left',
                            borderRadius: '6px',
                            transition: 'background 0.2s'
                          }}
                          className="hover:bg-red-50"
                        >
                          <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign Out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Link
                  to="/login"
                  style={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#64748b',
                    textDecoration: 'none',
                    transition: 'color 0.2s'
                  }}
                  className="hover:text-gray-900"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    background: '#1e293b',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: 400,
                    textDecoration: 'none',
                    transition: 'background 0.2s'
                  }}
                  className="hover:bg-gray-900"
                >
                  Sign Up
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: '#f8fafc',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s'
              }}
              className="md:hidden hover:bg-gray-200"
            >
              <svg style={{ width: '18px', height: '18px', stroke: '#64748b' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              style={{
                borderTop: '1px solid #f1f5f9',
                background: '#f8fafc',
                marginTop: '16px',
                paddingTop: '16px'
              }}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '0 8px' }}>
                <button
                  onClick={() => { handleDashboardClick(); closeMobileMenu(); }}
                  style={{
                    textAlign: 'left',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: isActive('/dashboard') ? 500 : 400,
                    color: isActive('/dashboard') ? '#1e293b' : '#64748b',
                    background: isActive('/dashboard') ? 'white' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  className="hover:bg-white"
                >
                  Home
                </button>
                <button
                  onClick={() => { navigate('/catalog'); closeMobileMenu(); }}
                  style={{
                    textAlign: 'left',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: isActive('/catalog') ? 500 : 400,
                    color: isActive('/catalog') ? '#1e293b' : '#64748b',
                    background: isActive('/catalog') ? 'white' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  className="hover:bg-white"
                >
                  Catalog
                </button>
                <button
                  onClick={() => { scrollToFeatures(); closeMobileMenu(); }}
                  style={{
                    textAlign: 'left',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#64748b',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  className="hover:bg-white"
                >
                  About
                </button>
                {isAuthenticated && (
                  <button
                    onClick={() => { handleRevealClick(); closeMobileMenu(); }}
                    style={{
                      textAlign: 'left',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'white',
                      background: '#1e293b',
                      border: 'none',
                      cursor: 'pointer',
                      marginTop: '8px',
                      transition: 'background 0.2s'
                    }}
                    className="hover:bg-gray-900"
                  >
                    + New Reveal
                  </button>
                )}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
